/* 文件解析：把各種格式變成「標題 ＋ 段落」的結構。
   PDF 用 pdfjs-dist，而且是**動態載入** —— 它很大，不該拖累開啟 App 的速度。 */
import { readZipEntries } from "./zip.ts";

export type DocKind = "text" | "markdown" | "html" | "docx" | "pdf" | "unknown";

export interface Block {
  kind: "heading" | "paragraph";
  level: number;          // heading 的層級，paragraph 固定 0
  text: string;
}

export interface ParsedDoc {
  kind: DocKind;
  title: string;
  blocks: Block[];
  text: string;           // 純文字，供搜尋與退回使用
  pageCount?: number;
}

export function detectKind(fileName: string, mime = ""): DocKind {
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  if (ext === "docx") return "docx";
  if (ext === "pdf" || mime === "application/pdf") return "pdf";
  if (ext === "md" || ext === "markdown") return "markdown";
  if (ext === "html" || ext === "htm") return "html";
  if (["txt", "text", "log", "csv", "json"].includes(ext) || mime.startsWith("text/")) return "text";
  return "unknown";
}

function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">").replace(/&quot;/gi, '"').replace(/&#39;/gi, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)));
}

function tidy(text: string): string {
  return decodeEntities(text).replace(/[ \t\u00a0]+/g, " ").replace(/\s+$/g, "").trim();
}

/** HTML：抓 h1–h6 當標題、p/li/div 當段落，其餘標籤去掉。不使用 DOMParser（Node 裡沒有）。 */
export function parseHtml(raw: string, title: string): ParsedDoc {
  const withoutNoise = raw
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<!--[\s\S]*?-->/g, " ");
  const blocks: Block[] = [];
  const re = /<(h[1-6])[^>]*>([\s\S]*?)<\/\1>|<(p|li|div|blockquote|td)[^>]*>([\s\S]*?)<\/\3>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(withoutNoise))) {
    const isHeading = !!m[1];
    const text = tidy((isHeading ? m[2] : m[4])!.replace(/<[^>]+>/g, " "));
    if (!text) continue;
    blocks.push({ kind: isHeading ? "heading" : "paragraph", level: isHeading ? Number(m[1]![1]) : 0, text });
  }
  if (!blocks.length) {
    const fallback = tidy(withoutNoise.replace(/<[^>]+>/g, " "));
    if (fallback) blocks.push({ kind: "paragraph", level: 0, text: fallback });
  }
  return { kind: "html", title, blocks, text: blocks.map((b) => b.text).join("\n") };
}

export function parseMarkdown(raw: string, title: string): ParsedDoc {
  const blocks: Block[] = [];
  const lines = raw.split(/\r?\n/);
  let buffer: string[] = [];
  const flush = () => {
    const text = tidy(buffer.join(" "));
    if (text) blocks.push({ kind: "paragraph", level: 0, text });
    buffer = [];
  };
  for (const line of lines) {
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: "heading", level: heading[1]!.length, text: tidy(heading[2]!) });
      continue;
    }
    if (!line.trim()) { flush(); continue; }
    buffer.push(line.replace(/^\s*[-*+]\s+/, "").replace(/[*_`]/g, ""));
  }
  flush();
  return { kind: "markdown", title, blocks, text: blocks.map((b) => b.text).join("\n") };
}

export function parsePlainText(raw: string, title: string): ParsedDoc {
  const blocks: Block[] = [];
  for (const chunk of raw.split(/\r?\n\s*\r?\n+/)) {
    const text = tidy(chunk);
    if (text) blocks.push({ kind: "paragraph", level: 0, text: text.replace(/\s*\r?\n\s*/g, " ") });
  }
  return { kind: "text", title, blocks, text: blocks.map((b) => b.text).join("\n") };
}

/** DOCX：zip 裡的 word/document.xml。標題靠 pStyle 的 Heading 樣式判斷。 */
export async function parseDocx(bytes: Uint8Array, title: string): Promise<ParsedDoc> {
  const files = await readZipEntries(bytes, ["word/document.xml"]);
  const xmlBytes = files.get("word/document.xml");
  if (!xmlBytes) throw new Error("這個 docx 裡找不到 word/document.xml（可能是舊版 .doc？）");
  const xml = new TextDecoder().decode(xmlBytes);
  const blocks: Block[] = [];
  const paragraphs = xml.match(/<w:p[ >][\s\S]*?<\/w:p>/g) ?? [];
  for (const paragraph of paragraphs) {
    const style = /<w:pStyle[^>]*w:val="([^"]+)"/.exec(paragraph);
    const text = tidy((paragraph.match(/<w:t[^>]*>([\s\S]*?)<\/w:t>/g) ?? [])
      .map((t) => t.replace(/<[^>]+>/g, "")).join(""));
    if (!text) continue;
    const heading = style && /heading\s*(\d)/i.exec(style[1]!);
    blocks.push(heading
      ? { kind: "heading", level: Number(heading[1]), text }
      : { kind: "paragraph", level: 0, text });
  }
  if (!blocks.length) throw new Error("這個 docx 抽不到任何文字。");
  return { kind: "docx", title, blocks, text: blocks.map((b) => b.text).join("\n") };
}

/* 只用得到這幾個成員。自己宣告最小介面，就不會綁死在 pdfjs 的型別版本上。 */
interface PdfTextItem { str?: string; transform?: number[] }
interface PdfPage { getTextContent(): Promise<{ items: unknown[] }> }
interface PdfDoc { numPages: number; getPage(n: number): Promise<PdfPage>; destroy(): Promise<void> }

/** PDF：動態載入 pdfjs-dist（它很大，不該拖累開啟 App 的速度）。
    掃描件抽不到文字 —— 那需要 OCR，而 OCR 依規劃放在桌面端。 */
export async function parsePdf(bytes: Uint8Array, title: string): Promise<ParsedDoc> {
  const pdfjs = (await import("pdfjs-dist/legacy/build/pdf.mjs")) as unknown as {
    getDocument(src: { data: Uint8Array }): { promise: Promise<PdfDoc> };
  };
  const doc: PdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
  const blocks: Block[] = [];
  for (let page = 1; page <= doc.numPages; page++) {
    const content = await (await doc.getPage(page)).getTextContent();
    let line = "";
    let lastY: number | null = null;
    for (const item of content.items as PdfTextItem[]) {
      if (typeof item.str !== "string") continue;
      const y = item.transform ? item.transform[5] ?? null : null;
      if (lastY !== null && y !== null && Math.abs(y - lastY) > 2) {
        const text = tidy(line);
        if (text) blocks.push({ kind: "paragraph", level: 0, text });
        line = "";
      }
      line += item.str;
      if (y !== null) lastY = y;
    }
    const tail = tidy(line);
    if (tail) blocks.push({ kind: "paragraph", level: 0, text: tail });
  }
  const pageCount = doc.numPages;
  await doc.destroy();
  const text = blocks.map((b) => b.text).join("\n");
  return { kind: "pdf", title, blocks, text, pageCount };
}

export async function parseFile(file: { name: string; type?: string; arrayBuffer(): Promise<ArrayBuffer> }): Promise<ParsedDoc> {
  const kind = detectKind(file.name, file.type ?? "");
  const title = file.name.replace(/\.[^.]+$/, "");
  if (kind === "unknown") throw new Error("還不支援這種格式：" + file.name);
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (kind === "docx") return parseDocx(bytes, title);
  if (kind === "pdf") return parsePdf(bytes, title);
  const raw = new TextDecoder().decode(bytes);
  if (kind === "html") return parseHtml(raw, title);
  if (kind === "markdown") return parseMarkdown(raw, title);
  return parsePlainText(raw, title);
}
